import { useState, useRef, useCallback, useEffect } from 'react';

export const TouchDial = ({ 
    value = 60, 
    onChange, 
    maxMinutes = 480, // 8 hours max
    onStart,
    isActive = false 
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const svgRef = useRef(null);
    const [displayValue, setDisplayValue] = useState(value);

    useEffect(() => {
        setDisplayValue(value);
    }, [value]);

    const radius = 120;
    const strokeWidth = 20;
    const center = 150;
    const circumference = 2 * Math.PI * radius;
    
    // 6-minute increments (0.1 hour = 1 tenth)
    const INCREMENT = 6;
    
    // Convert minutes to angle (0-360)
    const minutesToAngle = (mins) => (mins / maxMinutes) * 360;
    const angleToMinutes = (angle) => {
        // Round to nearest 6-minute increment
        const rawMinutes = (angle / 360) * maxMinutes;
        return Math.round(rawMinutes / INCREMENT) * INCREMENT;
    };
    
    const progress = displayValue / maxMinutes;
    const strokeDashoffset = circumference * (1 - progress);
    
    // Calculate handle position
    const angle = minutesToAngle(displayValue) - 90; // Start from top
    const handleX = center + radius * Math.cos((angle * Math.PI) / 180);
    const handleY = center + radius * Math.sin((angle * Math.PI) / 180);

    // Format as hours and tenths (legal billing format)
    const formatTime = (mins) => {
        const hours = Math.floor(mins / 60);
        const minutes = mins % 60;
        const tenths = mins / 6; // 6 min = 0.1 hour
        return {
            display: `${hours}:${minutes.toString().padStart(2, '0')}`,
            tenths: (mins / 60).toFixed(1),
            hours,
            minutes
        };
    };

    const timeInfo = formatTime(displayValue);

    const getAngleFromEvent = useCallback((clientX, clientY) => {
        if (!svgRef.current) return 0;
        const rect = svgRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const deltaX = clientX - centerX;
        const deltaY = clientY - centerY;
        let angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 90;
        if (angle < 0) angle += 360;
        return angle;
    }, []);

    const handleMove = useCallback((clientX, clientY) => {
        if (!isDragging) return;
        const angle = getAngleFromEvent(clientX, clientY);
        const newMinutes = angleToMinutes(angle);
        setDisplayValue(Math.max(INCREMENT, Math.min(maxMinutes, newMinutes)));
    }, [isDragging, getAngleFromEvent, maxMinutes]);

    const handleStart = useCallback((clientX, clientY) => {
        setIsDragging(true);
        const angle = getAngleFromEvent(clientX, clientY);
        const newMinutes = angleToMinutes(angle);
        setDisplayValue(Math.max(INCREMENT, Math.min(maxMinutes, newMinutes)));
    }, [getAngleFromEvent, maxMinutes]);

    const handleEnd = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            onChange?.(displayValue);
        }
    }, [isDragging, displayValue, onChange]);

    const onMouseDown = (e) => handleStart(e.clientX, e.clientY);
    const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const onMouseUp = () => handleEnd();
    const onTouchStart = (e) => { const touch = e.touches[0]; handleStart(touch.clientX, touch.clientY); };
    const onTouchMove = (e) => { const touch = e.touches[0]; handleMove(touch.clientX, touch.clientY); };
    const onTouchEnd = () => handleEnd();

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('touchmove', onTouchMove, { passive: false });
            window.addEventListener('touchend', onTouchEnd);
            return () => {
                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);
                window.removeEventListener('touchmove', onTouchMove);
                window.removeEventListener('touchend', onTouchEnd);
            };
        }
    }, [isDragging, onMouseMove, onMouseUp, onTouchMove, onTouchEnd]);

    // Quick select presets (in 6-minute increments)
    const presets = [
        { mins: 6, label: '0.1h' },
        { mins: 12, label: '0.2h' },
        { mins: 30, label: '0.5h' },
        { mins: 60, label: '1h' },
        { mins: 120, label: '2h' },
    ];

    return (
        <div className="relative flex flex-col items-center" data-testid="touch-dial">
            <svg 
                ref={svgRef}
                width="280" 
                height="280" 
                viewBox="0 0 300 300"
                className="touch-none select-none"
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}
            >
                {/* Background track with tick marks */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke="#E0E7FF"
                    strokeWidth={strokeWidth}
                    className="transition-all duration-200"
                />
                
                {/* Hour markers */}
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((hour) => {
                    const markerAngle = (hour / 8) * 360 - 90;
                    const innerR = radius - strokeWidth / 2 - 5;
                    const outerR = radius - strokeWidth / 2 + 5;
                    const x1 = center + innerR * Math.cos((markerAngle * Math.PI) / 180);
                    const y1 = center + innerR * Math.sin((markerAngle * Math.PI) / 180);
                    const x2 = center + outerR * Math.cos((markerAngle * Math.PI) / 180);
                    const y2 = center + outerR * Math.sin((markerAngle * Math.PI) / 180);
                    return (
                        <line
                            key={hour}
                            x1={x1} y1={y1} x2={x2} y2={y2}
                            stroke="#CBD5E1"
                            strokeWidth={2}
                        />
                    );
                })}
                
                {/* Progress arc */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius}
                    fill="none"
                    stroke={isActive ? "#10B981" : "url(#dialGradient)"}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${center} ${center})`}
                    className={`transition-all ${isDragging ? 'duration-0' : 'duration-200'}`}
                />
                
                {/* Gradient definition */}
                <defs>
                    <linearGradient id="dialGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#4F46E5" />
                        <stop offset="100%" stopColor="#7C3AED" />
                    </linearGradient>
                </defs>
                
                {/* Handle */}
                <circle
                    cx={handleX}
                    cy={handleY}
                    r={18}
                    fill="white"
                    stroke={isActive ? "#10B981" : "#4F46E5"}
                    strokeWidth={4}
                    className={`cursor-grab ${isDragging ? 'cursor-grabbing' : ''} drop-shadow-lg transition-transform ${isDragging ? 'scale-110' : ''}`}
                    style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' }}
                />
                
                {/* Inner circle background */}
                <circle
                    cx={center}
                    cy={center}
                    r={radius - 45}
                    fill="white"
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05))' }}
                />
            </svg>
            
            {/* Center content */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center" style={{ marginTop: '-14px' }}>
                <p className="font-display text-4xl font-bold tracking-tight text-foreground" data-testid="dial-time">
                    {timeInfo.display}
                </p>
                <p className="text-lg font-semibold text-indigo-600 mt-1">
                    {timeInfo.tenths} hours
                </p>
                <p className="text-xs text-muted-foreground">
                    {displayValue / 6} tenths
                </p>
            </div>
            
            {/* Quick select buttons */}
            <div className="flex gap-2 mt-4 flex-wrap justify-center">
                {presets.map((preset) => (
                    <button
                        key={preset.mins}
                        onClick={() => {
                            setDisplayValue(preset.mins);
                            onChange?.(preset.mins);
                        }}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            displayValue === preset.mins 
                                ? 'bg-primary text-white shadow-lg scale-105' 
                                : 'bg-secondary text-foreground hover:bg-slate-200 active:scale-95'
                        }`}
                        data-testid={`quick-${preset.mins}`}
                    >
                        {preset.label}
                    </button>
                ))}
            </div>
            
            {/* Increment buttons */}
            <div className="flex gap-4 mt-4">
                <button
                    onClick={() => {
                        const newVal = Math.max(INCREMENT, displayValue - INCREMENT);
                        setDisplayValue(newVal);
                        onChange?.(newVal);
                    }}
                    className="w-12 h-12 rounded-full bg-secondary text-foreground font-bold text-xl hover:bg-slate-200 transition-colors active:scale-95"
                    data-testid="decrement-btn"
                >
                    −
                </button>
                <button
                    onClick={() => {
                        const newVal = Math.min(maxMinutes, displayValue + INCREMENT);
                        setDisplayValue(newVal);
                        onChange?.(newVal);
                    }}
                    className="w-12 h-12 rounded-full bg-secondary text-foreground font-bold text-xl hover:bg-slate-200 transition-colors active:scale-95"
                    data-testid="increment-btn"
                >
                    +
                </button>
            </div>
            
            {/* Start/Stop button */}
            {onStart && (
                <button
                    onClick={() => onStart(displayValue)}
                    className={`mt-6 px-8 py-4 rounded-full text-lg font-semibold transition-all shadow-xl hover:shadow-2xl active:scale-95 ${
                        isActive 
                            ? 'bg-red-500 text-white hover:bg-red-600' 
                            : 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:from-indigo-600 hover:to-purple-600'
                    }`}
                    data-testid="dial-action-btn"
                >
                    {isActive ? 'Stop Timer' : 'Log Time'}
                </button>
            )}
        </div>
    );
};

export default TouchDial;
